import React, { useState } from 'react';
import { KpiRow, Panel, Empty, StackedBars, Legend, Segmented, usePersisted, fmtNum, fmtPct, rampColor } from './charts';

// Возвращаемость — по месяцам 1…6 (владелец 15.10: «нравилось как было»)
const COH = [1, 2, 3, 4, 5, 6];
// Покупают ли новички — от месяца прихода: тот же месяц, +1 … +5
const JOIN_COH = [0, 1, 2, 3, 4, 5];

/** Таблица «месяц → доля к 1-му, 2-му … месяцу»: заливка по силе, нажатие — пояснение */
function CohortGrid({ rows, cols, firstLabel, colLabel, cellText }) {
  const [cell, setCell] = useState(null);
  const maxPct = Math.max(1, ...rows.flatMap(c => c.cells.map(x => x.pct)));
  return (
    <div onMouseLeave={() => setCell(null)}>
      <div className="an-scroll-x">
        <div className="an-cohort" style={{ minWidth: 460 }}>
          <div className="an-cohort-row an-muted an-small">
            <span>{firstLabel}</span>{cols.map(k => <span key={k} className="c">{colLabel(k)}</span>)}
          </div>
          {rows.map(c => (
            <div key={c.month} className="an-cohort-row">
              <span>{c.month}<div className="an-muted an-small">{fmtNum(c.size)} чел.</div></span>
              {cols.map(k => {
                const x = c.cells.find(cc => cc.offset === k);
                if (!x) return <span key={k} />;
                const bg = rampColor(x.pct, maxPct);
                const dark = ['#3F8C64', '#1C4F36'].includes(bg);
                return (
                  <span key={k} className="an-cohort-cell" style={{ background: bg, color: dark ? '#fff' : '#1C3D2E', opacity: x.partial ? 0.6 : 1 }}
                    onMouseEnter={() => setCell(cellText(c, k, x))} onClick={() => setCell(cellText(c, k, x))}>
                    {fmtNum(x.pct, 0)} %{x.partial ? '*' : ''}
                  </span>
                );
              })}
            </div>
          ))}
        </div>
      </div>
      <div className="an-b an-small" style={{ minHeight: 18, marginTop: 6 }}>{cell || 'Нажмите на клетку'}</div>
      <div className="an-muted an-small">* месяц ещё идёт — цифра вырастет</div>
    </div>
  );
}

/** «По месяцам» — одна таблица, два взгляда (владелец 27.10: «два одинаковых блока»): пришедшие → купили
 *  (по месяцу прихода, с тем же месяцем) и купившие → купили снова (по месяцу первой покупки, 1…6 мес.) */
const MONTH_MODES = [{ value: 'joined', label: 'Пришли → купили' }, { value: 'repeat', label: 'Купили → купили снова' }];
function MonthsPanel({ joined, repeat }) {
  const [mode, setMode] = usePersisted('nf_an_months_mode', 'joined');
  const m = mode === 'repeat' || !joined.length ? 'repeat' : 'joined';
  return (
    <Panel title="По месяцам" aside={joined.length ? <Segmented small value={m} onChange={setMode} options={MONTH_MODES} /> : null}>
      <div className="an-muted an-small" style={{ marginBottom: 10 }}>
        {m === 'joined'
          ? 'Из пришедших в бота и на сайт за месяц — сколько купили к концу того же месяца, через месяц … через пять. Сравнивайте столбец сверху вниз: падает у свежих месяцев — новички стали покупать хуже.'
          : 'Из купивших впервые в месяце — сколько купили снова к 1-му, 2-му … 6-му месяцу. Сравнивайте столбец сверху вниз: падает у свежих месяцев — новые закрепляются хуже.'}
      </div>
      {m === 'joined' ? (
        <CohortGrid rows={joined} cols={JOIN_COH} firstLabel="Пришли" colLabel={(k) => (k ? `+${k} мес.` : 'тот же')}
          cellText={(c, k, x) => `${c.month}: из ${fmtNum(c.size)} пришедших к ${k ? `${k}-му месяцу после` : 'концу месяца'} купили ${fmtNum(x.back)} — ${fmtPct(x.pct)}${x.partial ? ' (месяц ещё идёт)' : ''}`} />
      ) : repeat.length ? (
        <CohortGrid rows={repeat} cols={COH} firstLabel="Первая покупка" colLabel={(k) => `${k} мес.`}
          cellText={(c, k, x) => `${c.month}: из ${c.size} новых за ${k} мес. купили снова ${x.back} — ${fmtPct(x.pct)}${x.partial ? ' (месяц ещё идёт)' : ''}`} />
      ) : <Empty>Пока мало истории — появится через пару месяцев продаж</Empty>}
    </Panel>
  );
}

/** «Пришли за период» (владелец 27.10): люди, а не только покупатели. Числа открывают список «Клиенты» */
function Audience({ a, period, compare, onOpenList }) {
  const came = a.funnel[0]?.count || 0;
  const span = { joined_from: period.from, joined_to: period.to };
  const step = { came: span, bought: { ...span, bought: 'any' }, repeat: { ...span, bought: 'many' } };
  const totals = Object.fromEntries(a.platforms.map(pl => [pl.key, a.series.reduce((s, b) => s + (b.values[pl.key] || 0), 0)]));
  return (
    <>
      <KpiRow tiles={a.tiles} compare={compare} />
      {onOpenList && (
        <div className="an-small an-mk-actions">
          <span className="an-muted">Открыть списком:</span>
          <button type="button" className="an-link" onClick={() => onOpenList(span)}>пришедшие за период</button>
          <button type="button" className="an-link" onClick={() => onOpenList({ ...span, bought: 'none' })}>из них ещё не купили</button>
          <button type="button" className="an-link" onClick={() => onOpenList({ reach: 'no' })}>кому сообщения не доходят</button>
        </div>
      )}

      <Panel title="Новые пользователи" aside={<span className="an-muted">{period.weekly ? 'по неделям' : 'по дням'}</span>}>
        {came ? (<>
          <StackedBars buckets={a.series} channels={a.platforms} fmt={fmtNum} unit={null} />
          <Legend items={a.platforms.map(pl => ({ ...pl, extra: fmtNum(totals[pl.key]) }))} />
        </>) : <Empty>За период никто не пришёл</Empty>}
      </Panel>

      <Panel title="Что стало с пришедшими" aside={<span className="an-muted">пришли за период — покупки до сегодня</span>}>
        {came ? (
          <div className="an-funnel">
            {a.funnel.map((f, i) => {
              const pct = came ? (f.count / came) * 100 : 0;
              const prev = i ? a.funnel[i - 1].count : null;
              return (
                <button key={f.key} type="button" className="an-funnel-row" disabled={!onOpenList}
                  onClick={() => onOpenList && onOpenList(step[f.key])}>
                  <span className="an-funnel-title">{f.title}</span>
                  <span className="an-funnel-bar"><span style={{ width: `${Math.max(pct, f.count ? 1 : 0)}%` }} /></span>
                  <b className="an-nowrap">{fmtNum(f.count)}</b>
                  <span className="an-muted an-small an-nowrap">{i ? `${fmtPct(pct, 1)} пришедших${i > 1 && prev ? ` · ${fmtPct((f.count / prev) * 100, 0)} от купивших` : ''}` : ''}</span>
                </button>
              );
            })}
          </div>
        ) : <Empty>За период никто не пришёл</Empty>}
        {a.pending && came > 0 && <div className="an-muted an-small" style={{ marginTop: 8 }}>Часть пришла недавно — купивших ещё прибавится.</div>}
      </Panel>

    </>
  );
}

/** «Клиенты»: сверху — пришли за период (27.10, период снова выбирается), ниже — база покупателей на сегодня
 *  (UX 15.10). Тепловая карта и рассылки убраны: «заказали после рассылки» было совпадением по датам. */
export default function CustomersTab({ data, blocks, compare, onNavigate }) {
  const has = (b) => blocks.includes(b);
  const today = new Date().toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
  // Переход в список «Клиенты» с фильтром: фильтр — разовой меткой, список читает её при открытии
  const openList = onNavigate ? (f) => {
    try { sessionStorage.setItem('nf_clients_filter', JSON.stringify(f)); } catch (e) { /* no-op */ }
    onNavigate('users');
  } : null;
  return (
    <div className="an-stack">
      {has('audience') && data.audience && <Audience a={data.audience} period={data.period} compare={compare} onOpenList={openList} />}

      <h2 className="an-subtitle" style={{ marginBottom: 0 }}>Покупатели на сегодня, {today}</h2>
      {has('customer_tiles') && <KpiRow tiles={data.tiles} compare={false} />}

      {has('segments') && (
        <Panel title="Группы покупателей">
          <div className="an-muted an-small" style={{ marginBottom: 10 }}>Все покупатели по давности и числу покупок, каждый — в одной группе. Доля — от всех покупателей.{openList ? ' Нажмите на группу — откроется список этих людей.' : ''}</div>
          <div className="an-segs">
            {data.segments.map(s => (
              <button key={s.key} type="button" className="an-seg-card" style={{ '--ch': '8px' }} disabled={!openList}
                onClick={() => openList && openList({ segment: s.key })}>
                <b>{s.title}</b>
                <span className="an-seg-num">{fmtNum(s.count)}{s.pct != null && <span className="an-muted an-seg-pct"> · {s.pct} %</span>}</span>
                <span className="an-muted an-small">{s.hint}</span>
              </button>
            ))}
          </div>
          <div className="an-muted an-small" style={{ marginTop: 10 }}>Покупатели Flowwow с тем же телефоном, что у аккаунта, считаются одним человеком. В списке — только люди с аккаунтом в боте или на сайте.</div>
        </Panel>
      )}

      {has('cohorts') && <MonthsPanel joined={data.audience?.cohorts || []} repeat={data.cohorts} />}
    </div>
  );
}
